import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { CheckCircle, XCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuoteResponse = () => {
  const { quoteId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');
  const action = searchParams.get('action');
  
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('');
  const [bookingId, setBookingId] = useState(null);
  
  useEffect(() => {
    if (!token || !action || !quoteId) {
      setStatus('error');
      setMessage('Lien invalide ou incomplet');
      return;
    }
    
    handleQuoteResponse();
  }, [quoteId, token, action]);
  
  const handleQuoteResponse = async () => {
    setLoading(true);
    
    try {
      const endpoint = action === 'accept' 
        ? `${API}/quotes/${quoteId}/accept?token=${token}`
        : `${API}/quotes/${quoteId}/decline?token=${token}`;
      
      const response = await axios.get(endpoint);
      
      if (response.data.success) {
        setStatus('success');
        setMessage(response.data.message);
        if (response.data.bookingId) {
          setBookingId(response.data.bookingId);
        }
        
        if (action === 'accept') {
          toast.success('Devis accepté ! Votre réservation a été confirmée.');
        } else {
          toast.info('Devis décliné.');
        }
      }
    } catch (error) {
      console.error('Error responding to quote:', error);
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Une erreur est survenue');
      toast.error(error.response?.data?.detail || 'Erreur lors du traitement');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Traitement en cours...</h2>
            <p className="text-slate-600">Veuillez patienter</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <Card className="shadow-xl">
          <CardContent className="pt-8 pb-8">
            {status === 'success' && action === 'accept' && (
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-6">
                  <CheckCircle className="w-12 h-12 text-green-600" />
                </div>
                
                <h1 className="text-3xl font-bold text-slate-900 mb-4">
                  Devis Accepté !
                </h1>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                  <p className="text-lg text-slate-700 mb-4">{message}</p>
                  
                  {bookingId && (
                    <div className="bg-white rounded-lg p-4 border border-green-300">
                      <p className="text-sm text-slate-600 mb-2">Numéro de réservation</p>
                      <p className="text-2xl font-bold text-blue-600">{bookingId}</p>
                    </div>
                  )}
                </div>
                
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                  <h3 className="font-semibold text-blue-900 mb-2">📧 Prochaines étapes</h3>
                  <ul className="text-left text-sm text-blue-800 space-y-2">
                    <li>✓ Un email de confirmation vous a été envoyé</li>
                    <li>✓ Votre réservation est confirmée</li>
                    <li>✓ Nous vous contacterons 24h avant votre transfert</li>
                  </ul>
                </div>
                
                <Button
                  onClick={() => navigate('/')}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Retour à l'accueil
                </Button>
              </div>
            )}
            
            {status === 'success' && action === 'decline' && (
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-slate-100 mb-6">
                  <XCircle className="w-12 h-12 text-slate-600" />
                </div>
                
                <h1 className="text-3xl font-bold text-slate-900 mb-4">
                  Devis Décliné
                </h1>
                
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-6">
                  <p className="text-lg text-slate-700 mb-4">{message}</p>
                </div>
                
                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-6">
                  <p className="text-sm text-amber-800">
                    💬 Si vous avez des questions ou souhaitez discuter d'une offre ajustée, 
                    n'hésitez pas à nous contacter.
                  </p>
                </div>
                
                <div className="flex gap-4 justify-center">
                  <Button
                    onClick={() => navigate('/')}
                    variant="outline"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Retour à l'accueil
                  </Button>
                  <Button
                    onClick={() => window.location.href = 'mailto:contact@luxurytransferservices.be'}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Nous contacter
                  </Button>
                </div>
              </div>
            )}
            
            {status === 'error' && (
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100 mb-6">
                  <AlertCircle className="w-12 h-12 text-red-600" />
                </div>
                
                <h1 className="text-3xl font-bold text-slate-900 mb-4">
                  Erreur
                </h1>
                
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
                  <p className="text-lg text-slate-700">{message}</p>
                </div>
                
                <div className="flex gap-4 justify-center">
                  <Button
                    onClick={() => navigate('/')}
                    variant="outline"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Retour à l'accueil
                  </Button>
                  <Button
                    onClick={() => window.location.href = 'mailto:contact@luxurytransferservices.be'}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Contacter le support
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Footer */}
        <div className="text-center mt-8">
          <div className="inline-block bg-white rounded-lg shadow-sm px-6 py-3">
            <p className="text-sm text-slate-600">
              <strong>Luxury Transfer Service</strong> - Service de transfert haut de gamme
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteResponse;
